import React, { FC, RefObject, useMemo, memo } from 'react'
import {
    AmenitiesPopupWrapper,
    TabBar,
    AmenitiesPopupTitle,
    Tab,
    RoomsWrapper,
    RoomsTitleDescription,
    RoomsDescription,
    RoomItems,
    RoomItem,
} from './styles'
import { Typography } from 'react-components'
import { useTranslation } from '../../../../hooks/locale'
import { FormattedRoomsResult } from '../../../../models/property-details-page/rooms-model'
import { FormattedSuitableFeatures } from '../../../../models/property-details-page/suitables-model'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

interface AmenitiesPopupContentProps {
    tabBarRef: RefObject<HTMLDivElement>
    rooms: FormattedRoomsResult[]
    activeRoom: number
    handleTabClick: (index: number) => void
    tabRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>
    roomRefs: React.MutableRefObject<(HTMLDivElement | null)[]>
    extendedFeatures: FormattedSuitableFeatures | null
}

const AmenitiesPopupContent: FC<AmenitiesPopupContentProps> = ({
    tabBarRef,
    rooms,
    activeRoom,
    handleTabClick,
    tabRefs,
    roomRefs,
    extendedFeatures,
}) => {
    const { t } = useTranslation()

    const roomTabs = useMemo(
        () =>
            rooms.map((room, index) => (
                <Tab
                    key={index}
                    isActive={index === activeRoom}
                    onClick={() => handleTabClick(index)}
                    ref={el => (tabRefs.current[index] = el)}
                >
                    <Typography
                        variant="subtitle1"
                        color={index === activeRoom ? 'black' : 'tfwBlack40'}
                        fontWeight={index === activeRoom ? 600 : 400}
                    >
                        {get(room, 'label', '') || ''}
                    </Typography>
                </Tab>
            )),
        [rooms, activeRoom, handleTabClick, tabRefs],
    )

    const extendedFeaturesTab = useMemo(() => {
        const extendedTitle = get(extendedFeatures, 'title', '')
        const extendedItems = get(extendedFeatures, 'items', [])

        if (isEmpty(extendedItems)) {
            return null
        }

        return (
            <Tab
                key={rooms.length}
                isActive={rooms.length === activeRoom}
                onClick={() => handleTabClick(rooms.length)}
                ref={el => (tabRefs.current[rooms.length] = el)}
            >
                <Typography
                    variant="subtitle1"
                    color={rooms.length === activeRoom ? 'black' : 'tfwBlack40'}
                    fontWeight={rooms.length === activeRoom ? 600 : 400}
                >
                    {extendedTitle || ''}
                </Typography>
            </Tab>
        )
    }, [rooms.length, activeRoom, handleTabClick, tabRefs, extendedFeatures])

    const renderRoomDetails = useMemo(
        () =>
            rooms.map((room, index) => {
                const roomDescription = get(room, 'description', []) || []
                const roomItems = get(room, 'items', []) || []

                return (
                    <RoomsWrapper key={index} ref={el => (roomRefs.current[index] = el)}>
                        <RoomsTitleDescription>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {get(room, 'label', '') || ''}
                            </Typography>
                            <RoomsDescription>
                                {Array.isArray(roomDescription) &&
                                    roomDescription.map((description: string, descIndex: number) => (
                                        <Typography
                                            key={descIndex}
                                            variant="subtitle2"
                                            color="tfwBlack40"
                                            whiteSpace="pre"
                                        >
                                            {descIndex > 0 && ' • '}
                                            {description}
                                        </Typography>
                                    ))}
                            </RoomsDescription>
                        </RoomsTitleDescription>
                        <RoomItems>
                            {Array.isArray(roomItems) &&
                                roomItems.map((item: string, itemIndex: number) => (
                                    <RoomItem key={itemIndex}>
                                        <Typography variant="subtitle2" whiteSpace="pre">
                                            {` • ${item}`}
                                        </Typography>
                                    </RoomItem>
                                ))}
                        </RoomItems>
                    </RoomsWrapper>
                )
            }),
        [rooms, roomRefs],
    )

    const renderExtendedFeaturesDetails = useMemo(() => {
        const extendedTitle = get(extendedFeatures, 'title', '') || ''
        const extendedItems = get(extendedFeatures, 'items', []) || []

        if (isEmpty(extendedItems)) {
            return null
        }

        return (
            <RoomsWrapper key={rooms.length} ref={el => (roomRefs.current[rooms.length] = el)}>
                <RoomsTitleDescription>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {extendedTitle}
                    </Typography>
                </RoomsTitleDescription>
                <RoomItems>
                    {Array.isArray(extendedItems) &&
                        extendedItems.map((item: string, itemIndex: number) => (
                            <RoomItem key={itemIndex}>
                                <Typography variant="subtitle2" whiteSpace="pre">
                                    {` • ${item}`}
                                </Typography>
                            </RoomItem>
                        ))}
                </RoomItems>
            </RoomsWrapper>
        )
    }, [rooms.length, extendedFeatures, roomRefs])

    return (
        <AmenitiesPopupWrapper>
            <AmenitiesPopupTitle>
                <Typography variant="subtitle1" fontWeight="bold">
                    {t('amenitiesTitle')}
                </Typography>
            </AmenitiesPopupTitle>
            <TabBar ref={tabBarRef}>
                {roomTabs}
                {extendedFeaturesTab}
            </TabBar>
            {renderRoomDetails}
            {renderExtendedFeaturesDetails}
        </AmenitiesPopupWrapper>
    )
}

export default memo(AmenitiesPopupContent)
